import _ from 'lodash';
import moment from 'moment-timezone';
import { nodeTypes } from 'data/trident-types';

export const CRM_PARQUET_TIME_PREFIX_FORMAT = 'year/month/day/hour/minute';

export interface ParquetPreviewContext {
  nodeKey: string;
  parquetPayload: Record<string, any>;
}

export interface CampaignParquetPreviewAction {
  key: string;
  label: string;
  actionNodeKey: string;
  actionIndex: number;
  actionType: string;
  templateID: string;
  templateName: string;
  params: Record<string, any>;
}

export interface CampaignParquetPreviewSource {
  key: string;
  label: string;
  scheduleNodeKey: string;
  enumerationNodeKey: string;
  parquetPayload: Record<string, any>;
  lastHappenedAt: any;
  actions: CampaignParquetPreviewAction[];
}

export interface ParquetFetchRequest {
  filePrefix: string;
  key: string;
  userID: string;
  bucket?: string;
}

export interface ParquetTimeOption {
  key: string;
  value: string;
  text: string;
  date: string;
  time: string;
}

export interface ParquetDateTimeFields {
  date: string;
  time: string;
}

const DATE_TIME_FORMATS = ['YYYY-MM-DD HH:mm', 'YYYY-MM-DD HH:mm:ss'];

const toTrimmedString = (value: any): string => {
  if (_.isNil(value)) {
    return '';
  }
  return String(value).trim();
};

const trimPath = (value: any): string => toTrimmedString(value).replace(/^\/+|\/+$/g, '');

const stripFilePrefix = (path: string, filePrefix: string): string => {
  const normalizedPrefix = trimPath(filePrefix);
  if (!normalizedPrefix) {
    return path;
  }
  if (path === normalizedPrefix) {
    return '';
  }
  if (path.indexOf(`${normalizedPrefix}/`) === 0) {
    return path.slice(normalizedPrefix.length + 1);
  }
  return path;
};

const toMoment = (value: any): moment.Moment | null => {
  if (_.isNil(value) || value === '') {
    return null;
  }
  const parsed = moment.isMoment(value) ? value.clone() : moment(value);
  return parsed.isValid() ? parsed : null;
};

const getModelNodes = (model: Record<string, any>): Record<string, any> => {
  const nodes = _.get(model, 'nodes', {});
  if (_.isString(nodes)) {
    try {
      return JSON.parse(nodes);
    } catch (error) {
      return {};
    }
  }
  return nodes || {};
};

const isValidTimezone = (timezoneName?: string): boolean =>
  !_.isEmpty(timezoneName) && !!moment.tz.zone(String(timezoneName));

const toCampaignMoment = (
  value: any,
  timezoneName?: string,
): moment.Moment | null => {
  const parsed = toMoment(value);
  if (!parsed) {
    return null;
  }
  if (isValidTimezone(timezoneName)) {
    return parsed.tz(String(timezoneName));
  }
  return parsed;
};

export const buildScheduleMomentInUTC = (
  selectedDate: string,
  selectedTime: string,
  timezoneName?: string,
): moment.Moment | null => {
  const date = toTrimmedString(selectedDate);
  const time = toTrimmedString(selectedTime);
  if (!date || !time) {
    return null;
  }

  const dateTime = `${date} ${time}`;
  const parsed = isValidTimezone(timezoneName)
    ? moment.tz(dateTime, DATE_TIME_FORMATS, true, String(timezoneName))
    : moment(dateTime, DATE_TIME_FORMATS, true);

  if (!parsed.isValid()) {
    return null;
  }
  return parsed.utc();
};

const toParquetDateTimeFields = (value: moment.Moment): ParquetDateTimeFields => ({
  date: value.format('YYYY-MM-DD'),
  time: value.format('HH:mm'),
});

const fallbackDateTimeFields = (
  selectedDate: string,
  selectedTime: string,
): ParquetDateTimeFields => ({
  date: toTrimmedString(selectedDate),
  time: toTrimmedString(selectedTime),
});

export const convertCampaignDateTimeFieldsToUTC = (
  selectedDate: string,
  selectedTime: string,
  timezoneName?: string,
): ParquetDateTimeFields => {
  const triggerTime = buildScheduleMomentInUTC(
    selectedDate,
    selectedTime,
    timezoneName,
  );
  return triggerTime
    ? toParquetDateTimeFields(triggerTime)
    : fallbackDateTimeFields(selectedDate, selectedTime);
};

export const convertUTCDateTimeFieldsToCampaign = (
  selectedDate: string,
  selectedTime: string,
  timezoneName?: string,
): ParquetDateTimeFields => {
  const date = toTrimmedString(selectedDate);
  const time = toTrimmedString(selectedTime);
  if (!date || !time) {
    return fallbackDateTimeFields(selectedDate, selectedTime);
  }

  const parsed = moment.utc(`${date} ${time}`, DATE_TIME_FORMATS, true);
  if (!parsed.isValid()) {
    return fallbackDateTimeFields(selectedDate, selectedTime);
  }

  const campaignTime = isValidTimezone(timezoneName)
    ? parsed.tz(String(timezoneName))
    : parsed;
  return toParquetDateTimeFields(campaignTime);
};

export const getEnumerationParquetFilepath = (payload: Record<string, any>): string =>
  trimPath(_.get(payload, 'parquetPayload.filepath'));

export const findNearestEnumerationWithFilepath = (
  model: Record<string, any>,
  parentKeyList: string[] = [],
): ParquetPreviewContext | null => {
  const nodes = getModelNodes(model);
  for (let index = parentKeyList.length - 1; index >= 0; index -= 1) {
    const nodeKey = parentKeyList[index];
    const node = _.get(nodes, nodeKey, {});
    const parquetPayload = _.get(node, 'data.payload.parquetPayload');
    if (
      _.get(node, 'type') === nodeTypes.enumeration &&
      !_.isEmpty(trimPath(_.get(parquetPayload, 'filepath')))
    ) {
      return {
        nodeKey,
        parquetPayload,
      };
    }
  }
  return null;
};

export const findNearestScheduleNodeKey = (
  model: Record<string, any>,
  parentKeyList: string[] = [],
): string => {
  const nodes = getModelNodes(model);
  for (let index = parentKeyList.length - 1; index >= 0; index -= 1) {
    const nodeKey = parentKeyList[index];
    if (_.get(nodes, `${nodeKey}.type`) === nodeTypes.schedule) {
      return nodeKey;
    }
  }
  return '';
};

export const getScheduleProgressKey = (
  model: Record<string, any>,
  parentKeyList: string[] = [],
): string => {
  const scheduleNodeKey = findNearestScheduleNodeKey(model, parentKeyList);
  if (!scheduleNodeKey) {
    return '';
  }
  return String(_.get(model, `rootNodeScheduleMapping[${scheduleNodeKey}]`, ''));
};

export const getScheduleProgressParams = (
  model: Record<string, any>,
  parentKeyList: string[] = [],
): Record<string, any> | null => {
  const nodes = getModelNodes(model);
  const scheduleNodeKey = findNearestScheduleNodeKey(model, parentKeyList);
  const scheduleID = _.get(model, `rootNodeScheduleMapping[${scheduleNodeKey}]`);
  const lastHappenedAt = _.get(nodes, `${scheduleNodeKey}.data.lastHappenedAt`);
  const triggerTime = toMoment(lastHappenedAt);

  if (!scheduleID || !triggerTime) {
    return null;
  }

  return {
    scheduleID,
    triggerTime: triggerTime.format(),
  };
};

const collectPreviewActions = (
  nodes: Record<string, any>,
  nodeKey: string,
  visited: Record<string, boolean> = {},
): CampaignParquetPreviewAction[] => {
  if (!nodeKey || visited[nodeKey]) {
    return [];
  }
  visited[nodeKey] = true;

  const node = _.get(nodes, nodeKey, {});
  let actions: CampaignParquetPreviewAction[] = [];

  if (_.get(node, 'type') === nodeTypes.actions) {
    actions = _.compact(_.map(_.get(node, 'data', []), (actionData, actionIndex) => {
      const templateID = toTrimmedString(_.get(actionData, 'meta.messagingPayload.templateID'));
      if (!templateID) {
        return null;
      }
      const templateName = toTrimmedString(_.get(actionData, 'meta.messagingPayload.templateName'));
      return {
        key: `${nodeKey}:${actionIndex}`,
        label: templateName || `Action ${nodeKey}.${actionIndex + 1}`,
        actionNodeKey: nodeKey,
        actionIndex,
        actionType: toTrimmedString(_.get(actionData, 'type')),
        templateID,
        templateName,
        params: _.get(actionData, 'meta.messagingPayload.params', {}),
      };
    }));
  }

  _.forEach(_.get(node, 'children', []), childNodeKey => {
    actions = [
      ...actions,
      ...collectPreviewActions(nodes, String(childNodeKey), visited),
    ];
  });

  return actions;
};

const collectEnumerationSources = (
  nodes: Record<string, any>,
  scheduleNodeKey: string,
  nodeKey: string,
  visited: Record<string, boolean> = {},
): CampaignParquetPreviewSource[] => {
  if (!nodeKey || visited[nodeKey]) {
    return [];
  }
  visited[nodeKey] = true;

  const node = _.get(nodes, nodeKey, {});
  const parquetPayload = _.get(node, 'data.payload.parquetPayload');
  let sources: CampaignParquetPreviewSource[] = [];

  if (
    _.get(node, 'type') === nodeTypes.enumeration &&
    !_.isEmpty(trimPath(_.get(parquetPayload, 'filepath')))
  ) {
    sources.push({
      key: `${scheduleNodeKey}:${nodeKey}`,
      label: toTrimmedString(_.get(node, 'name')) || `Enumeration ${nodeKey}`,
      scheduleNodeKey,
      enumerationNodeKey: nodeKey,
      parquetPayload,
      lastHappenedAt: _.get(nodes, `${scheduleNodeKey}.data.lastHappenedAt`),
      actions: collectPreviewActions(nodes, nodeKey),
    });
  }

  _.forEach(_.get(node, 'children', []), childNodeKey => {
    sources = [
      ...sources,
      ...collectEnumerationSources(
        nodes,
        scheduleNodeKey,
        String(childNodeKey),
        visited,
      ),
    ];
  });

  return sources;
};

export const findCampaignParquetPreviewSources = (
  model: Record<string, any>,
): CampaignParquetPreviewSource[] => {
  const nodes = getModelNodes(model);
  let sources: CampaignParquetPreviewSource[] = [];

  _.forEach(nodes, (node, nodeKey) => {
    if (_.get(node, 'type') !== nodeTypes.schedule) {
      return;
    }
    _.forEach(_.get(node, 'children', []), childNodeKey => {
      sources = [
        ...sources,
        ...collectEnumerationSources(
          nodes,
          String(nodeKey),
          String(childNodeKey),
        ),
      ];
    });
  });

  return sources;
};

export const buildParquetTimeOptions = (
  progress: Record<string, any> = {},
  lastHappenedAt?: any,
  timezoneName?: string,
): ParquetTimeOption[] => {
  const reportTimes = _.map(_.get(progress, 'reports', []), report => _.get(report, 'triggeredTime'));
  const moments = _.compact(_.map(
    [...reportTimes, lastHappenedAt],
    value => toCampaignMoment(value, timezoneName),
  ));

  return _.chain(moments)
    .uniqBy(item => item.toISOString())
    .sortBy(item => -item.valueOf())
    .map(item => ({
      key: item.toISOString(),
      value: item.toISOString(),
      text: item.format('YYYY-MM-DD HH:mm'),
      date: item.format('YYYY-MM-DD'),
      time: item.format('HH:mm'),
    }))
    .value();
};

export const getDefaultParquetDateTimeFields = (
  timeOptions: ParquetTimeOption[],
): ParquetDateTimeFields => ({
  date: _.get(timeOptions, '0.date', ''),
  time: _.get(timeOptions, '0.time', ''),
});

export const buildParquetFetchRequest = (
  parquetPayload: Record<string, any>,
  selectedDate: string,
  selectedTime: string,
  timezoneName: string,
  userID: string,
): ParquetFetchRequest => {
  const filepath = trimPath(_.get(parquetPayload, 'filepath'));
  const timePrefixFormat = toTrimmedString(_.get(parquetPayload, 'timePrefixFormat'));
  let filePrefix = trimPath(_.get(parquetPayload, 'filePrefix'));

  if (!filePrefix) {
    filePrefix = 'enumeration';
  }

  if (!filepath) {
    throw new Error('Parquet filepath is required');
  }

  let key = stripFilePrefix(filepath, filePrefix);
  if (timePrefixFormat === CRM_PARQUET_TIME_PREFIX_FORMAT) {
    const triggerTime = buildScheduleMomentInUTC(
      selectedDate,
      selectedTime,
      timezoneName,
    );
    if (!triggerTime) {
      throw new Error('Trigger time is required');
    }
    key = [
      `year=${triggerTime.format('YYYY')}`,
      `month=${triggerTime.format('MM')}`,
      `day=${triggerTime.format('DD')}`,
      `hour=${triggerTime.format('HH')}`,
      `minute=${triggerTime.format('mm')}`,
      key,
    ].filter(Boolean).join('/');
  }

  const request: ParquetFetchRequest = {
    filePrefix,
    key,
    userID: toTrimmedString(userID),
  };
  const bucket = toTrimmedString(_.get(parquetPayload, 'bucket'));
  if (bucket) {
    request.bucket = bucket;
  }
  return request;
};

export const getParquetPreviewKey = (
  currentNode: string,
  subActionIndex: number,
  request: ParquetFetchRequest,
): string => [
  currentNode,
  subActionIndex,
  request.userID,
  request.filePrefix,
  request.key,
].join(':');
